import { parsePersistedCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import api from '@/soluqr/api/client';
import { withCustomerSession } from '@/lib/customerSession';
import { readLocalCustomer } from '@/lib/localCustomer';
import { normalizeCustomerPhone } from '@/lib/phone';
import { idKey } from '@/lib/assistant/assistantLineKeys';
import { stripAssistantWelcomeProductMeta } from '@/lib/assistantWelcomeProducts';
import { trackAssistantEvent } from '@/lib/assistantAnalytics';
import { createAssistantProductFlow } from '@/lib/assistantProductFlow';
import { createAssistantUpsellFlow } from '@/lib/assistantUpsellFlow';
import { isDemoBusinessId } from '@/lib/demo/demoBusiness';

const PRODUCTS_PER_PAGE = 3;

/** Carrito, upsell, product flow y handlers de agregar producto. */
export function bindAssistantChatCart(ctx) {
  const {
    businessId,
    menuData,
    cart,
    addToCart,
    addClubRewardToCart,
    mergeAddOnsIntoLatestLineForProduct,
    getCartCount,
    benefitsProgressSnapshot,
    setBenefitsProgressSnapshot,
    setPendingProducts,
    pendingProductsRef,
    setCurrentProductContext,
    setCurrentStep,
    productFlowRef,
    assistantAnalyticsCtx,
    addBotMessage,
    buildAfterUpsellClosingText,
    trackClosingPromptShown,
    openAssistantCheckout,
    showCheckoutPreferencePrompt,
    showCartSummaryInChat,
    getHandleMainChoice,
    showBackToMenu,
    showProductActions,
    handleMainChoice,
    handleOrderChoice,
    handleOrderChoiceFromText,
    focusAssistantChatInput,
  } = ctx;

  const getCartQtyForProductId = (productId) => {
    const k = idKey(productId);
    if (!k) return 0;
    return (cart || []).reduce((sum, line) => {
      if (idKey(line.product?.id) !== k) return sum;
      return sum + (Number(line.quantity) || 1);
    }, 0);
  };

  const getCartLinesForUpsell = () => {
    try {
      const raw = localStorage.getItem('soluqr_cart');
      const { cart: lines, businessId: bid } = parsePersistedCart(raw);
      if (lines.length && bid === businessId) return lines;
    } catch {
      // ignore
    }
    return cart;
  };

  const productFlowApi = createAssistantProductFlow({
    getMenuData: () => menuData,
    productsPerPage: PRODUCTS_PER_PAGE,
    setPendingProducts,
    pendingProductsRef,
    setCurrentProductContext,
    setCurrentStep,
    addBotMessage,
    addUserMessage: ctx.addUserMessage,
    showBackToMenu,
    showProductActions,
    handleMainChoice,
    handleOrderChoice,
    handleOrderChoiceFromText,
    focusAssistantChatInput,
  });
  productFlowRef.current = productFlowApi;

  const { showUpsellAfterAdd, showAfterAddActions } = createAssistantUpsellFlow({
    getMenuData: () => menuData,
    getCartLinesForUpsell,
    openAssistantCheckout,
    showCheckoutPreferencePrompt,
    showCartSummaryInChat,
    handleMainChoice,
    continueShoppingFromAssistant: () => productFlowApi.continueShoppingFromAssistant(),
    addBotMessage,
    buildAfterUpsellClosingText,
    trackClosingPromptShown,
  });

  const handleClubRewardFromWelcome = async (product, lineQty) => {
    if (isDemoBusinessId(businessId)) {
      toast.message('En la demo no se canjean premios reales.');
      return;
    }
    const phone = normalizeCustomerPhone(readLocalCustomer(businessId)?.phone || '');
    if (!phone) {
      toast.error('Necesitamos tu teléfono en tu perfil para canjear el premio.');
      return;
    }
    const avail = Math.max(0, Number(benefitsProgressSnapshot?.available_rewards) || 0);
    if (avail < 1) {
      toast.error('No tienes premios disponibles para canjear.');
      return;
    }
    const want = Math.max(1, Math.min(99, Number(lineQty) || 1));
    const qty = Math.min(want, avail);
    if (want > avail) {
      toast.message(`Solo puedes canjear hasta ${avail} premio${avail === 1 ? '' : 's'} en este momento.`);
    }
    try {
      const { data } = await api.post(
        '/customers/redeem-benefit',
        {
          business_id: businessId,
          phone,
          count: qty,
        },
        withCustomerSession({}, businessId),
      );
      const progress = data?.progress;
      if (progress) setBenefitsProgressSnapshot(progress);
      const clean = stripAssistantWelcomeProductMeta(product);
      const label =
        String(product.clubRewardDisplayLabel || '').trim() ||
        String(progress?.reward_text || '').trim() ||
        clean?.name ||
        'tu premio';
      addClubRewardToCart(clean, qty, [], [], {
        clubRewardLabel: label,
        silent: true,
        businessId,
      });
      trackAssistantEvent('assistant_add_to_cart_clicked', {
        ...assistantAnalyticsCtx(null),
        product_id: clean?.id ?? null,
        source: 'assistant_club_reward_welcome',
        quantity: qty,
      });
      const remaining = Math.max(0, Number(progress?.available_rewards) || 0);
      let msg = `Canjeaste ${qty} premio${qty > 1 ? 's' : ''}: ${label} 🎁`;
      if (remaining === 1) msg += '\n\nTe queda 1 premio disponible.';
      else if (remaining > 1) msg += `\n\nTe quedan ${remaining} premios disponibles.`;
      else msg += '\n\nYa no te quedan premios por canjear.';
      addBotMessage(msg, null, [clean], { productCardMode: 'confirmation' });
      setTimeout(() => showAfterAddActions(), 360);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const err =
        typeof d === 'string' ? d : Array.isArray(d) ? d[0]?.msg : 'No se pudo canjear el premio';
      toast.error(err);
    }
  };

  const handleAddToCart = (
    product,
    skipNextUpsell = false,
    addSource = 'assistant_main',
    quantity = 1
  ) => {
    const qty = Math.max(1, Math.min(99, Number(quantity) || 1));
    const prevCartQty = getCartQtyForProductId(product?.id);
    trackAssistantEvent('assistant_add_to_cart_clicked', {
      ...assistantAnalyticsCtx(null),
      product_id: product?.id ?? null,
      source: addSource,
      quantity: qty,
    });
    const newLineId = addToCart(product, [], [], qty, { silent: true, businessId });
    const totalNow = prevCartQty + qty;
    const confirmText =
      totalNow > qty
        ? `Listo: llevas ${totalNow}× ${product?.name || 'este producto'} en el carrito 👌`
        : qty > 1
          ? `Listo: ${qty}× ${product?.name || 'Producto'} en el carrito 👌`
          : `Listo: ${product?.name || 'Producto'} en el carrito 👌`;
    addBotMessage(confirmText, null, [product], {
      productCardMode: 'confirmation',
    });
    if (skipNextUpsell) {
      setTimeout(() => showAfterAddActions(), 360);
      return;
    }
    setTimeout(() => showUpsellAfterAdd(product, newLineId), 360);
  };

  const handlePostAddonAdd = (row, lineQty) => {
    const ok = mergeAddOnsIntoLatestLineForProduct(
      row.productId,
      [{ name: row.name, price: row.price, quantity: lineQty }],
      row.cartLineId ? { cartLineId: row.cartLineId, businessId } : { businessId }
    );
    if (!ok) {
      toast.error('No encontré el producto en el carrito.');
      return;
    }
    trackAssistantEvent('assistant_quick_addon_clicked', {
      ...assistantAnalyticsCtx(null),
      product_id: row.productId ?? null,
      addon_name: row.name,
      addon_price: row.price,
      addon_quantity: lineQty,
    });
    addBotMessage(
      lineQty > 1
        ? `Listo: sumé ${lineQty}× ${row.name} a tu pedido.`
        : `Listo: sumé ${row.name} a tu pedido.`
    );
  };

  const handleCatalogProductAdd = ({ product, message, lineQty, isWelcomeClubReward }) => {
    if (isWelcomeClubReward) {
      handleClubRewardFromWelcome(product, lineQty);
      return;
    }
    handleAddToCart(
      product,
      message.skipUpsellForProductAdds === true,
      'assistant_main',
      lineQty
    );
  };

  const handleSuggestedProductAdd = ({ product, message, lineQty }) => {
    handleAddToCart(
      product,
      message.skipUpsellForProductAdds === true,
      'assistant_contextual',
      lineQty
    );
  };

  return {
    getCartQtyForProductId,
    getCartLinesForUpsell,
    handleAddToCart,
    handleClubRewardFromWelcome,
    handlePostAddonAdd,
    handleCatalogProductAdd,
    handleSuggestedProductAdd,
    productFlowApi,
  };
}
